import { Form } from 'antd';
import { ProductionItemDetail, SeedBatch } from 'constants/interface';
import { buildRegularProductDisplay, buildSimpleQuantityDisplay } from 'helpers/order';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'hooks/store';
import {
  Input,
  InputNumber,
  Modal,
  Table,
  Tag,
} from 'UI';
import { InventoryTypeDisplayMapping, InventoryTypeEnum } from 'constants/inventory';
import { useAvailableSeedBatches } from 'hooks/availableSeedBatches';
import { formatNumber, formatStringDate } from 'helpers/utils';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { updateSeedReservationAction } from 'state/slices/productionItem';
import { unwrapResult } from '@reduxjs/toolkit';

import {
  InlineButton,
  MainContainer,
  FormContainer,
  ItemInfoContainer,
  ItemInfoRow,
  ItemInfoLabel,
  ItemInfoValue,
  BaseFormItem,
  AggregatorContainer,
  TotalQuantityLabel,
  SeedBatchesSkeleton,
} from './style';

interface SeedReservationModalProps {
  item: ProductionItemDetail
  isVisible: boolean
  onCancel: () => void
  onSuccess: () => void
}

interface PopulatedSeedBatch extends SeedBatch {
  reservedQuantity?: string
}

interface SeedReservationFormData {
  batches: PopulatedSeedBatch[],
}

const populateBatchesQuantity = (
  availableBatches: SeedBatch[],
  targetQuantity: number,
  currentlyReservedMap?: Record<string, SeedBatch>,
): PopulatedSeedBatch[] => {
  let quantityLimiter = targetQuantity;
  const populatedSeedBatches = availableBatches.map((seedBatch) => {
    if (currentlyReservedMap) {
      // If previous reservations have been made,
      // populate with them

      const populatedSeedBatch: PopulatedSeedBatch = {
        ...seedBatch,
        reservedQuantity: `${currentlyReservedMap[seedBatch.batch]?.quantity || ''}`,
      };

      return populatedSeedBatch;
    }

    // If no previous reservations have been made,
    // populate with all available batches
    if (quantityLimiter <= 0) {
      const notPopulatedSeedBatch: PopulatedSeedBatch = {
        ...seedBatch,
        reservedQuantity: undefined,
      };

      return notPopulatedSeedBatch;
    }

    quantityLimiter -= seedBatch.quantity;
    const populatedQuantity = quantityLimiter > 0
      ? seedBatch.quantity
      : seedBatch.quantity + quantityLimiter;
    const populatedSeedBatch: PopulatedSeedBatch = {
      ...seedBatch,
      reservedQuantity: `${populatedQuantity}`,
    };

    return populatedSeedBatch;
  });

  return populatedSeedBatches;
};

const SeedReservationModal = ({
  item,
  isVisible,
  onCancel,
  onSuccess,
}: SeedReservationModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);
  const [totalQuantity, setTotalQuantity] = useState(0);

  const [seedReservationForm] = Form.useForm<SeedReservationFormData>();

  const calculateTotalQuantity = () => {
    const formbatches = seedReservationForm.getFieldValue('batches') as PopulatedSeedBatch[];
    const updatedTotalQuantity = formbatches
      .reduce((qty, batch) => (qty + (parseInt(batch.reservedQuantity || '0', 10))), 0);
    setTotalQuantity(updatedTotalQuantity);
  };

  const currentlyReservedMap = useMemo(() => {
    const reservedMap: Record<string, SeedBatch> = {};

    item
      .seedReservations
      .forEach((reservation) => { reservedMap[reservation.batch] = reservation; });

    return Object.values(reservedMap).length > 0 ? reservedMap : undefined;
  }, [item]);

  const {
    isLoading: isLoadingAvailableSeedBatches,
    batches: availableSeedBatches,
  } = useAvailableSeedBatches(item.id, isVisible);

  useEffect(() => {
    const populatedSeedBatches = populateBatchesQuantity(
      availableSeedBatches || [],
      item.calculatedQuantity,
      currentlyReservedMap,
    );
    seedReservationForm.setFields([{
      name: 'batches',
      value: populatedSeedBatches,
    }]);
    calculateTotalQuantity();
  }, [availableSeedBatches]);

  const seedBatches = availableSeedBatches || [];

  const submitReservation = async (): Promise<void> => {
    try {
      await seedReservationForm.validateFields();
    } catch (e) {
      const errorAlert = createAlertNotification(
        'Verifique os campos com erro.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      return;
    }
    const formData = seedReservationForm.getFieldsValue();

    // const parsedBatch: {
    //   batch: string
    //   quantity: number
    // } = {
    //   batch: formSeedBatch.batch,
    //   quantity: parseInt(formSeedBatch.reservedQuantity || '0', 10)
    // }

    const parsedData = formData
      .batches
      .filter((formSeedBatch): formSeedBatch is PopulatedSeedBatch & {
        reservedQuantity: string
      } => (!!formSeedBatch.reservedQuantity))
      .map((formSeedBatch) => {
        const parsedBatch: {
          batch: string
          quantity: number
        } = {
          batch: formSeedBatch.batch,
          quantity: parseInt(formSeedBatch.reservedQuantity, 10),
        };

        return parsedBatch;
      });

    try {
      setLoading(true);
      const result = await dispatch(updateSeedReservationAction({
        id: item.id,
        data: {
          batches: parsedData,
        },
      }));
      setLoading(false);
      unwrapResult(result);
      onSuccess();
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao tentar reservar sementes. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={isVisible}
      title="Reserva de sementes"
      destroyOnClose
      onCancel={onCancel}
      afterClose={onCancel}
      zIndex={90}
      footer={[
        <InlineButton
          key="cancel"
          onClick={onCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={submitReservation}
          disabled={isLoading || totalQuantity < item.calculatedQuantity}
          loading={isLoading}
        >
          Salvar
        </InlineButton>,
      ]}
    >
      <MainContainer>
        <ItemInfoContainer>
          <ItemInfoRow>
            <ItemInfoLabel>Cultivar:</ItemInfoLabel>
            <ItemInfoValue>
              {`${buildRegularProductDisplay(item, {
                quantity: false,
                tray: false,
              })}`}
            </ItemInfoValue>
          </ItemInfoRow>
          <ItemInfoRow>
            <ItemInfoLabel>Quantidade prevista:</ItemInfoLabel>
            <ItemInfoValue>
              {
                buildSimpleQuantityDisplay(item.calculatedQuantity)
              }
            </ItemInfoValue>
          </ItemInfoRow>
          <ItemInfoRow>
            <ItemInfoLabel>Estoque:</ItemInfoLabel>
            <ItemInfoValue>
              {
                item.seedSource === InventoryTypeEnum.CLIENT
                  ? item.orderItem.order.client.name
                  : InventoryTypeDisplayMapping[item.seedSource].name
              }
            </ItemInfoValue>
          </ItemInfoRow>
        </ItemInfoContainer>
        <AggregatorContainer>
          <ItemInfoRow>
            <ItemInfoLabel>Quantidade total:</ItemInfoLabel>
            <TotalQuantityLabel isInvalid={(totalQuantity < item.calculatedQuantity)}>
              {formatNumber(totalQuantity)}
              {
                totalQuantity < item.calculatedQuantity
                  ? (
                    <Tag color="error">Insuficiente</Tag>
                  ) : null
              }
            </TotalQuantityLabel>
          </ItemInfoRow>
        </AggregatorContainer>
        {
          isLoadingAvailableSeedBatches
            ? (
              <SeedBatchesSkeleton
                loading
                active
                paragraph={{
                  rows: 3,
                  width: '100%',
                }}
                title={false}
              />
            ) : (
              <FormContainer
                layout="vertical"
                form={seedReservationForm}
                requiredMark={false}
                validateTrigger="onSubmit"
                // initialValues={{
                //   seedingDate: mainSeedingBatch?.seedingDate
                //     ? dayjs(mainSeedingBatch.seedingDate)
                //     : dayjs(item.orderItem.plannedSeedDate),
                //   greenhouseId: mainSeedingBatch?.greenhouse?.id || undefined,
                //   notes: mainSeedingBatch?.notes || undefined,
                // }}
              >
                <Form.List
                  name="batches"
                  rules={[{
                    message: 'Insira pelo menos um item',
                    validator: () => {
                      // TODO: check this logic
                      const formItems = [1];
                      if (formItems.length > 0) {
                        return Promise.resolve();
                      }

                      return Promise.reject();
                    },
                  }]}
                >
                  {() => (
                    <Table
                      dataSource={seedBatches}
                      pagination={false}
                      rowKey="batch"
                      scroll={{
                        y: '260px',
                      }}
                    >
                      <Table.Column
                        dataIndex="batch"
                        title="Lote"
                        render={(value, record: SeedBatch) => {
                          const { batch } = record;
                          return batch;
                        }}
                      />
                      <Table.Column
                        dataIndex="quantity"
                        title="Disponível"
                        render={(value, record: SeedBatch) => {
                          const { quantity } = record;
                          return formatNumber(quantity);
                        }}
                      />
                      <Table.Column
                        dataIndex="expiryDate"
                        title="Validade"
                        render={(value, record: SeedBatch) => {
                          const { expiryDate } = record;
                          return formatStringDate(expiryDate);
                        }}
                      />
                      <Table.Column
                        dataIndex="reservedQuantity"
                        title="Quantidade"
                        render={(value, record: SeedBatch, rowIndex) => (
                          <>
                            <BaseFormItem
                              name={[rowIndex, 'reservedQuantity']}
                            >
                              <InputNumber
                                // type="number"
                                max={record.quantity}
                                onChange={calculateTotalQuantity}
                              />
                            </BaseFormItem>
                            <BaseFormItem
                              name={[rowIndex, 'batch']}
                              hidden
                            >
                              <Input hidden value={record.batch} />
                            </BaseFormItem>
                          </>
                        )}
                      />
                    </Table>
                  )}
                </Form.List>
              </FormContainer>
            )
        }
      </MainContainer>
    </Modal>
  );
};

export default SeedReservationModal;
